import React, { useEffect, useState } from "react";
import { Calendar, DollarSign, Crown, Building, TrendingUp } from 'lucide-react';
import * as Icons from 'lucide-react';
import { CalendarWidget } from "./calendarwigget";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CalendarView } from "./calendarview";
import { communities } from "../../../redux/community/action";
import { useAppDispatch } from "../../../hooks/hooks";
import { useSelector } from "react-redux";
import { selectCommunitiesListItems } from "../../../redux/community/selector";


// Communities Hub – Enhanced with Business & Growth Features
// New Features: Premium Memberships, Analytics Dashboard, Integration Marketplace, Community Templates

/*********** Utilities ***********/
const cls = (...parts) => parts.filter(Boolean).join(" ");
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getHashId = (href) => {
    if (!href || href[0] !== "#") return null;
    const after = href.slice(1);
    const id = after.split(/[?&]/)[0].trim();
    return id || null;
};

const resolveIcon = (name) => {
    if (!name || typeof name !== "string") return Icons.Users;
    return Icons[name] || Icons.Users;
};

const SafeLink = ({ href = "#", className = "", children, onClick }) => {
    const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
    if (isExternal) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
                {children}
            </a>
        );
    }
    const handleClick = (e) => {
        e.preventDefault();
        if (onClick) onClick(e);
        if (href && href.startsWith("#")) {
            window.location.hash = href;
            const id = getHashId(href);
            const el = id ? document.getElementById(id) : null;
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };
    return (
        <a href={href} className={className} onClick={handleClick}>
            {children}
        </a>
    );
};

/*********** Enhanced Mock Data ***********/
const MOCK_COMMUNITIES = [
    {
        id: "ondc",
        name: "ONDC Adoption Circle",
        pillar: "DPI",
        icon: "Network",
        members: 1320,
        about: "Seller onboarding, logistics, network growth.",
        isPremium: true,
        monthlyRevenue: 15600,
        engagementRate: 78,
        template: "marketplace"
    },
    {
        id: "ulip",
        name: "ULIP Logistics Hub",
        pillar: "DPI",
        icon: "Truck",
        members: 980,
        about: "Unified logistics interface for MSMEs & fleets.",
        isPremium: false,
        monthlyRevenue: 0,
        engagementRate: 65,
        template: "technical"
    },
    {
        id: "we",
        name: "Women Maker Guild",
        pillar: "Women",
        icon: "Users",
        members: 1680,
        about: "Products -> platforms: brand, sell, scale.",
        isPremium: true,
        monthlyRevenue: 22400,
        engagementRate: 85,
        template: "entrepreneurship"
    },
    {
        id: "fmcg",
        name: "FMCG & Personal Care",
        pillar: "Industry",
        icon: "Building2",
        members: 990,
        about: "Distributor -> Agent models, pricebooks.",
        isPremium: false,
        monthlyRevenue: 0,
        engagementRate: 58,
        template: "industry"
    },
    {
        id: "esg",
        name: "Climate & Circularity",
        pillar: "ESG",
        icon: "Leaf",
        members: 610,
        about: "Reverse logistics, green ops, carbon math.",
        isPremium: true,
        monthlyRevenue: 8900,
        engagementRate: 72,
        template: "sustainability"
    }
];

const COMMUNITY_TEMPLATES = [
    {
        id: "marketplace",
        name: "Marketplace Community",
        description: "For platforms connecting buyers and sellers",
        icon: "ShoppingCart",
        features: ["Seller onboarding", "Transaction support", "Dispute resolution", "Analytics dashboard"],
        pricing: { setup: 2500, monthly: 199 },
        integrations: ["payment-gateway", "logistics-api", "analytics-pro"]
    },
    {
        id: "technical",
        name: "Developer Community",
        description: "For technical teams and API ecosystems",
        icon: "Code",
        features: ["API documentation", "Code sharing", "Technical support", "Integration guides"],
        pricing: { setup: 1500, monthly: 149 },
        integrations: ["github", "slack", "documentation-hub"]
    },
    {
        id: "entrepreneurship",
        name: "Entrepreneur Network",
        description: "For startup founders and business builders",
        icon: "Rocket",
        features: ["Mentorship matching", "Pitch practice", "Funding resources", "Success tracking"],
        pricing: { setup: 3000, monthly: 249 },
        integrations: ["calendar", "video-calls", "crm-basic"]
    },
    {
        id: "industry",
        name: "Industry Association",
        description: "For trade associations and industry groups",
        icon: "Building",
        features: ["Member directory", "Industry news", "Compliance updates", "Networking events"],
        pricing: { setup: 2000, monthly: 179 },
        integrations: ["newsletter", "event-management", "member-portal"]
    },
    {
        id: "sustainability",
        name: "Sustainability Hub",
        description: "For ESG and environmental initiatives",
        icon: "TreePine",
        features: ["Impact tracking", "Resource sharing", "Best practices", "Certification support"],
        pricing: { setup: 2200, monthly: 189 },
        integrations: ["carbon-calculator", "impact-tracker", "reporting-tools"]
    }
];

const INTEGRATION_MARKETPLACE = [
    {
        id: "payment-gateway",
        name: "Payment Gateway Pro",
        description: "Accept payments for premium memberships and events",
        icon: "CreditCard",
        category: "Monetization",
        price: 49,
        rating: 4.8,
        installs: 2340,
        features: ["Stripe integration", "Subscription management", "Invoice generation"]
    },
    {
        id: "analytics-pro",
        name: "Analytics Pro",
        description: "Advanced community analytics and insights",
        icon: "BarChart3",
        category: "Analytics",
        price: 79,
        rating: 4.9,
        installs: 1890,
        features: ["Custom dashboards", "Export reports", "Predictive insights"]
    },
    {
        id: "video-calls",
        name: "Video Conferencing",
        description: "Integrated video calls and webinars",
        icon: "Video",
        category: "Communication",
        price: 39,
        rating: 4.7,
        installs: 3120,
        features: ["HD video calls", "Screen sharing", "Recording"]
    },
    {
        id: "automation-hub",
        name: "Automation Hub",
        description: "Workflow automation and smart notifications",
        icon: "Zap",
        category: "Productivity",
        price: 59,
        rating: 4.6,
        installs: 1560,
        features: ["Custom workflows", "Smart triggers", "Integration builder"]
    },
    {
        id: "content-ai",
        name: "Content AI Assistant",
        description: "AI-powered content generation and moderation",
        icon: "Brain",
        category: "AI Tools",
        price: 99,
        rating: 4.5,
        installs: 890,
        features: ["Content suggestions", "Auto-moderation", "Translation"]
    },
    {
        id: "event-management",
        name: "Event Management Pro",
        description: "Advanced event planning and ticketing",
        icon: "Calendar",
        category: "Events",
        price: 69,
        rating: 4.8,
        installs: 2100,
        features: ["Ticket sales", "Event analytics", "Attendee management"]
    }
];

const MOCK_EVENTS = {
    ondc: [
        { id: "e1", title: "Seller Bootcamp", date: "2025-09-05", time: "14:00", rsvped: false, type: "workshop", attendees: 45, isPaid: true, price: 299 },
        { id: "e6", title: "ONDC Success Stories", date: "2025-09-15", time: "16:00", rsvped: false, type: "webinar", attendees: 120, isPaid: false, price: 0 }
    ],
    ulip: [
        { id: "e2", title: "ULIP API Onboarding", date: "2025-09-12", time: "10:00", rsvped: true, type: "training", attendees: 32, isPaid: false, price: 0 }
    ],
    we: [
        { id: "e3", title: "WE Demo Day", date: "2025-09-20", time: "18:00", rsvped: false, type: "showcase", attendees: 200, isPaid: true, price: 199 },
        { id: "e7", title: "Women in Tech Panel", date: "2025-09-25", time: "19:00", rsvped: true, type: "panel", attendees: 150, isPaid: false, price: 0 }
    ],
    fmcg: [
        { id: "e4", title: "Distributor -> Agent Playbook", date: "2025-10-01", time: "15:00", rsvped: false, type: "workshop", attendees: 28, isPaid: true, price: 399 }
    ],
    esg: [
        { id: "e5", title: "Reverse Logistics Roundtable", date: "2025-09-22", time: "11:00", rsvped: false, type: "roundtable", attendees: 35, isPaid: false, price: 0 }
    ]
};

const now = Date.now();
const MOCK_FEED = {
    ondc: [{ id: "p1", author: "Sara", text: "Checklist for ONDC seller onboarding.", ts: now - 3600000, likes: 12, comments: 3, type: "resource" }],
    ulip: [{ id: "p2", author: "Raj", text: "ULIP token flow diagram.", ts: now - 7200000, likes: 8, comments: 1, type: "resource" }],
    we: [{ id: "p3", author: "Priya", text: "Success story: 10x month-on-month growth!", ts: now - 10800000, likes: 24, comments: 7, type: "success" }],
    fmcg: [{ id: "p4", author: "Karthik", text: "Pricebook policy template v2.", ts: now - 5400000, likes: 6, comments: 2, type: "resource" }],
    esg: [{ id: "p5", author: "Anita", text: "Carbon calculator spreadsheet (alpha).", ts: now - 9300000, likes: 15, comments: 4, type: "resource" }]
};

// Mock analytics data
const generateAnalytics = (communityId) => {
    const base = MOCK_COMMUNITIES.find(c => c.id === communityId);
    if (!base) return null;
    // console.log(communitylist,"communitylist")

    return {
        revenue: {
            monthly: base.monthlyRevenue,
            growth: Math.floor(Math.random() * 30) + 10,
            subscriptions: Math.floor(base.monthlyRevenue / 25),
            events: Math.floor(base.monthlyRevenue * 0.3)
        },
        engagement: {
            rate: base.engagementRate,
            posts: Math.floor(Math.random() * 50) + 20,
            comments: Math.floor(Math.random() * 200) + 100,
            activeUsers: Math.floor(base.members * 0.4)
        },
        growth: {
            newMembers: Math.floor(Math.random() * 20) + 5,
            retention: Math.floor(Math.random() * 15) + 80,
            referrals: Math.floor(Math.random() * 10) + 3
        }
    };
};

/*********** Local Storage Keys ***********/
const LS_AUTH = "hub.auth";
const LS_MY_COMMUNITIES = "hub.my.communities";
const LS_USER_PLAN = "hub.user.plan";
const LS_INSTALLED_INTEGRATIONS = "hub.integrations";

/*********** Hash Router ***********/
const parseHash = (h) => {
    let raw = typeof h === "string" ? h : "";
    if (!raw || raw === "#") raw = "#/login";
    if (!raw.startsWith("#")) raw = "#/login";
    const qIndex = raw.indexOf("?");
    const path = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
    const query = qIndex >= 0 ? raw.slice(qIndex + 1) : "";
    let params = {};
    try {
        params = query && query.includes("=") ? Object.fromEntries(new URLSearchParams(query)) : {};
    } catch (e) {
        params = {};
    }
    return { path, params };
};

const useHashRoute = () => {
    const parse = () => parseHash(window.location.hash || "#/login");
    const [route, setRoute] = useState(parse());
    useEffect(() => {
        const fn = () => setRoute(parse());
        window.addEventListener("hashchange", fn);
        return () => window.removeEventListener("hashchange", fn);
    }, []);
    return route;
};

/*********** Auth ***********/
const useAuth = () => {
    const [user, setUser] = useState(() => {
        const raw = localStorage.getItem(LS_AUTH);
        return raw ? JSON.parse(raw) : null;
    });

    const [userPlan, setUserPlan] = useState(() => {
        const raw = localStorage.getItem(LS_USER_PLAN);
        return raw ? JSON.parse(raw) : { type: "free", features: ["basic-communities", "basic-analytics"] };
    });

    const login = async (email) => {
        await sleep(400);
        const u = { id: "u1", name: email.split("@")[0], email, role: "admin" };
        localStorage.setItem(LS_AUTH, JSON.stringify(u));
        setUser(u);
        if (!localStorage.getItem(LS_MY_COMMUNITIES)) {
            localStorage.setItem(LS_MY_COMMUNITIES, JSON.stringify(["ondc", "we"]));
        }
    };

    const logout = () => {
        localStorage.removeItem(LS_AUTH);
        setUser(null);
    };

    const upgradePlan = (planType) => {
        const plans = {
            pro: { type: "pro", features: ["premium-communities", "advanced-analytics", "monetization", "integrations"], price: 49 },
            enterprise: { type: "enterprise", features: ["all-features", "white-label", "custom-integrations", "priority-support"], price: 199 }
        };
        const newPlan = plans[planType] || userPlan;
        localStorage.setItem(LS_USER_PLAN, JSON.stringify(newPlan));
        setUserPlan(newPlan);
    };

    return { user, login, logout, userPlan, upgradePlan };
};

/*********** UI Primitives ***********/
// const Button = (props = {}) => {
//     const Comp = props && props.as ? props.as : "button";
//     const { className = "", children, variant = "default" } = props;
//     const rest = { ...props };
//     delete rest.as;
//     delete rest.children;
//     delete rest.variant;

//     const variants = {
//         default: "bg-slate-100 hover:bg-slate-200 text-slate-900",
//         primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
//         success: "bg-emerald-600 hover:bg-emerald-700 text-white",
//         premium: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white",
//         outline: "border border-slate-300 hover:bg-slate-50 text-slate-700"
//     };

//     return (
//         <Comp className={cls("inline-flex items-center gap-2 rounded-xl px-4 py-2 font-semibold transition-all", variants[variant], className)} {...rest}>
//             {children}
//         </Comp>
//     );
// };

const Card = ({ className = "", children }) => (
    <div className={cls("rounded-2xl shadow-lg bg-white border border-slate-200", className)}>{children}</div>
);

const Section = ({ children, className = "" }) => (
    <section className={cls("max-w-7xl mx-auto px-4 sm:px-6 lg:px-8", className)}>{children}</section>
);

const Badge = ({ children, variant = "default", className = "" }) => {
    const variants = {
        default: "bg-slate-100 text-slate-700",
        premium: "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700",
        success: "bg-emerald-100 text-emerald-700",
        warning: "bg-amber-100 text-amber-700"
    };
    return (
        <span className={cls("inline-flex items-center px-2 py-1 rounded-full text-xs font-medium", variants[variant], className)}>
            {children}
        </span>
    );
};
const variants = {
    default: "bg-slate-100 hover:bg-slate-200 text-slate-900",
    primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
    premium: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white",
    outline: "border border-slate-300 hover:bg-slate-50 text-slate-700"
};


const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;



    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <Card className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h2 className="text-xl font-bold">{title}</h2>
                    <button className={variants?.outline} onClick={onClose}>
                        <Icons.X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </Card>
        </div>
    );
};

/*********** Business Features ***********/
const MonetizationDashboard = ({ community }) => {
    const analytics = generateAnalytics(community.id);
    const variants = {
        default: "bg-slate-100 hover:bg-slate-200 text-slate-900",
        primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
        success: "bg-emerald-600 hover:bg-emerald-700 text-white",
        premium: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white",
        outline: "border border-slate-300 hover:bg-slate-50 text-slate-700"
    };

    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-2 text-emerald-600 mb-2">
                        <Icons.DollarSign className="w-5 h-5" />
                        <span className="font-semibold">Monthly Revenue</span>
                    </div>
                    <div className="text-2xl font-bold">${analytics.revenue.monthly.toLocaleString()}</div>
                    <div className="text-sm text-emerald-600">+{analytics.revenue.growth}% this month</div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-2 text-purple-600 mb-2">
                        <Icons.Users className="w-5 h-5" />
                        <span className="font-semibold">Premium Members</span>
                    </div>
                    <div className="text-2xl font-bold">{analytics.revenue.subscriptions}</div>
                    <div className="text-sm text-slate-500">Active subscriptions</div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2">
                        <Icons.Calendar className="w-5 h-5" />
                        <span className="font-semibold">Event Revenue</span>
                    </div>
                    <div className="text-2xl font-bold">${analytics.revenue.events.toLocaleString()}</div>
                    <div className="text-sm text-slate-500">From paid events</div>
                </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                    <h3 className="font-bold mb-4">Premium Membership Tiers</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                            <div>
                                <div className="font-semibold">Basic Premium</div>
                                <div className="text-sm text-slate-500">Access to exclusive content</div>
                            </div>
                            <div className="text-right">
                                <div className="font-bold">$19/mo</div>
                                <div className="text-sm text-emerald-600">142 members</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-3 border border-purple-200 rounded-lg bg-purple-50">
                            <div>
                                <div className="font-semibold">Pro Premium</div>
                                <div className="text-sm text-slate-500">1-on-1 mentoring + events</div>
                            </div>
                            <div className="text-right">
                                <div className="font-bold">$49/mo</div>
                                <div className="text-sm text-purple-600">89 members</div>
                            </div>
                        </div>
                    </div>
                    <button className={`w-full mt-4 ${variants?.primary}`}>
                        <Icons.Settings className="w-4 h-4" /> Manage Tiers
                    </button>

                </Card>

                <Card className="p-6">
                    <h3 className="font-bold mb-4">Sponsored Content</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                            <div>
                                <div className="font-semibold">Featured Posts</div>
                                <div className="text-sm text-slate-500">Highlight sponsor content</div>
                            </div>
                            <div className="text-sm text-emerald-600">$200/week</div>
                        </div>
                        <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                            <div>
                                <div className="font-semibold">Newsletter Ads</div>
                                <div className="text-sm text-slate-500">Weekly newsletter placement</div>
                            </div>
                            <div className="text-sm text-emerald-600">$150/week</div>
                        </div>
                    </div>
                    <button className={`w-full mt-4 ${variants?.success}`}>
                        <Icons.Plus className="w-4 h-4" /> Add Sponsor
                    </button>
                </Card>
            </div>
        </div>
    );
};

// nitish
const AnalyticsDashboard = ({ community }) => {
    const analytics = generateAnalytics(community.id);

    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2">
                        <Icons.TrendingUp className="w-5 h-5" />
                        <span className="font-semibold">Engagement</span>
                    </div>
                    <div className="text-2xl font-bold">{analytics.engagement.rate}%</div>
                    <div className="text-sm text-slate-500">Weekly average</div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-2 text-emerald-600 mb-2">
                        <Icons.UserPlus className="w-5 h-5" />
                        <span className="font-semibold">New Members</span>
                    </div>
                    <div className="text-2xl font-bold">{analytics.growth.newMembers}</div>
                    <div className="text-sm text-slate-500">This week</div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-2 text-purple-600 mb-2">
                        <Icons.MessageSquare className="w-5 h-5" />
                        <span className="font-semibold">Posts</span>
                    </div>
                    <div className="text-2xl font-bold">{analytics.engagement.posts}</div>
                    <div className="text-sm text-slate-500">This week</div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-2 text-amber-600 mb-2">
                        <Icons.Target className="w-5 h-5" />
                        <span className="font-semibold">Retention</span>
                    </div>
                    <div className="text-2xl font-bold">{analytics.growth.retention}%</div>
                    <div className="text-sm text-slate-500">30-day retention</div>
                </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                    <h3 className="font-bold mb-4">Top Contributors</h3>
                    <div className="space-y-3">
                        {["Priya", "Sara", "Karthik", "Anita"].map((name, i) => (
                            <div key={name} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                                        {name[0]}
                                    </div>
                                    <span className="font-medium">{name}</span>
                                </div>
                                <div className="text-sm text-slate-500">{50 - i * 10} points</div>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-bold mb-4">Content Performance</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm">Most liked post</span>
                            <span className="text-sm font-semibold">24 likes</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm">Average comments</span>
                            <span className="text-sm font-semibold">3.2 per post</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm">Peak activity time</span>
                            <span className="text-sm font-semibold">2-4 PM</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm">Popular content type</span>
                            <span className="text-sm font-semibold">Resources</span>
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="p-6">
                <h3 className="font-bold mb-4">Growth Insights</h3>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <Icons.TrendingUp className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                        <div className="font-semibold">Growth Rate</div>
                        <div className="text-2xl font-bold text-emerald-600">+{analytics.growth.newMembers}%</div>
                        <div className="text-sm text-slate-500">Month over month</div>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <Icons.Users className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                        <div className="font-semibold">Active Users</div>
                        <div className="text-2xl font-bold text-indigo-600">{analytics.engagement.activeUsers}</div>
                        <div className="text-sm text-slate-500">Weekly active</div>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <Icons.Share className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                        <div className="font-semibold">Referrals</div>
                        <div className="text-2xl font-bold text-purple-600">{analytics.growth.referrals}</div>
                        <div className="text-sm text-slate-500">This month</div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const IntegrationMarketplace = ({ userPlan }) => {
    const [installedIntegrations, setInstalledIntegrations] = useState(() => {
        const raw = localStorage.getItem(LS_INSTALLED_INTEGRATIONS);
        return raw ? JSON.parse(raw) : [];
    });

    const [selectedCategory, setSelectedCategory] = useState("all");
    const categories = ["all", "Monetization", "Analytics", "Communication", "Productivity", "AI Tools", "Events"];

    const filteredIntegrations = selectedCategory === "all"
        ? INTEGRATION_MARKETPLACE
        : INTEGRATION_MARKETPLACE.filter(i => i.category === selectedCategory);

    const installIntegration = (integrationId) => {
        if (!userPlan.features.includes("integrations") && !userPlan.features.includes("all-features")) {
            alert("Upgrade to Pro plan to install integrations!");
            return;
        }

        const newInstalled = [...installedIntegrations, integrationId];
        setInstalledIntegrations(newInstalled);
        localStorage.setItem(LS_INSTALLED_INTEGRATIONS, JSON.stringify(newInstalled));
    };

    const uninstallIntegration = (integrationId) => {
        const newInstalled = installedIntegrations.filter(id => id !== integrationId);
        setInstalledIntegrations(newInstalled);
        localStorage.setItem(LS_INSTALLED_INTEGRATIONS, JSON.stringify(newInstalled));
    };

    return (
        <Section className="py-8">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold">Integration Marketplace</h2>
                    <p className="text-slate-600">Extend your communities with powerful third-party tools</p>
                </div>
                <Badge variant="premium">
                    <Icons.Zap className="w-3 h-3" />
                    {installedIntegrations.length} installed
                </Badge>
            </div>

            <div className="flex gap-2 mb-6 flex-wrap">
                {categories.map(cat => (
                    <button
                        key={cat}
                        className={`capitalize ${selectedCategory === cat ? variants.primary : variants.outline
                            }`}
                        onClick={() => setSelectedCategory(cat)}
                    >\
                        {cat}
                    </button>
                ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredIntegrations.map(integration => {
                    const Icon = resolveIcon(integration.icon);
                    const isInstalled = installedIntegrations.includes(integration.id);

                    return (
                        <Card key={integration.id} className="p-6 hover:shadow-xl transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold">{integration.name}</h3>
                                        <Badge variant="default">{integration.category}</Badge>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold">${integration.price}/mo</div>
                                    <div className="flex items-center gap-1 text-sm text-amber-500">
                                        <Icons.Star className="w-3 h-3 fill-current" />
                                        {integration.rating}
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-slate-600 mb-4">{integration.description}</p>

                            <div className="space-y-2 mb-4">
                                {integration.features.map(feature => (
                                    <div key={feature} className="flex items-center gap-2 text-sm">
                                        <Icons.Check className="w-3 h-3 text-emerald-600" />
                                        {feature}
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                                <span>{integration.installs.toLocaleString()} installs</span>
                                <span>Updated recently</span>
                            </div>

                            {isInstalled ? (
                                <div className="space-y-2">
                                    <button className={`w-full ${variants?.success}`} disabled>
                                        <Icons.Check className="w-4 h-4" /> Installed
                                    </button>
                                    <button className={`w-full ${variants?.outline}`} onClick={() => uninstallIntegration(integration.id)}>
                                        <Icons.Trash2 className="w-4 h-4" /> Uninstall
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className={`w-full ${variants?.primary}`}
                                    onClick={() => installIntegration(integration.id)}
                                >
                                    <Icons.Download className="w-4 h-4" /> Install
                                </button>
                            )}
                        </Card>
                    );
                })}
            </div>
        </Section>
    );
};

const CommunityTemplates = ({ userPlan, onCreateCommunity }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    return (
        <Section className="py-8">
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Community Templates</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                    Launch your community faster with industry-specific templates. Each template includes
                    pre-configured features, content guidelines, and integration recommendations.
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {COMMUNITY_TEMPLATES.map(template => {
                    const Icon = resolveIcon(template.icon);

                    return (
                        <Card key={template.id} className="p-6 hover:shadow-xl transition-all hover:-translate-y-1">
                            <div className="text-center mb-4">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-3">
                                    <Icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="font-bold text-lg">{template.name}</h3>
                                <p className="text-sm text-slate-600 mt-2">{template.description}</p>
                            </div>

                            <div className="space-y-2 mb-4">
                                <div className="font-semibold text-sm">Included Features:</div>
                                {template.features.map(feature => (
                                    <div key={feature} className="flex items-center gap-2 text-sm">
                                        <Icons.Check className="w-3 h-3 text-emerald-600" />
                                        {feature}
                                    </div>
                                ))}
                            </div>

                            <div className="border-t border-slate-200 pt-4 mb-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-semibold">Setup Fee</span>
                                    <span className="font-bold">${template.pricing.setup}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-semibold">Monthly</span>
                                    <span className="font-bold">${template.pricing.monthly}/mo</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <button
                                    className={`w-full ${variants?.primary}`}
                                    onClick={() => setSelectedTemplate(template)}
                                >
                                    <Icons.Eye className="w-4 h-4" /> Preview Template
                                </button>
                                <button
                                    className={`w-full ${variants?.success}`}
                                    onClick={() => onCreateCommunity(template)}
                                >
                                    <Icons.Plus className="w-4 h-4" /> Use Template
                                </button>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {/* Template Preview Modal */}
            <Modal
                isOpen={!!selectedTemplate}
                onClose={() => setSelectedTemplate(null)}
                title={selectedTemplate?.name || "Template Preview"}
            >
                {selectedTemplate && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                {React.createElement(resolveIcon(selectedTemplate.icon), { className: "w-8 h-8 text-white" })}
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">{selectedTemplate.name}</h3>
                                <p className="text-slate-600">{selectedTemplate.description}</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-bold mb-3">Features Included</h4>
                                <div className="space-y-2">
                                    {selectedTemplate.features.map(feature => (
                                        <div key={feature} className="flex items-center gap-2">
                                            <Icons.Check className="w-4 h-4 text-emerald-600" />
                                            <span className="text-sm">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h4 className="font-bold mb-3">Recommended Integrations</h4>
                                <div className="space-y-2">
                                    {selectedTemplate.integrations.map(integrationId => {
                                        const integration = INTEGRATION_MARKETPLACE.find(i => i.id === integrationId);
                                        return integration ? (
                                            <div key={integrationId} className="flex items-center gap-2">
                                                <Icons.Puzzle className="w-4 h-4 text-indigo-600" />
                                                <span className="text-sm">{integration.name}</span>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-50 p-4 rounded-lg">
                            <h4 className="font-bold mb-2">Pricing</h4>
                            <div className="flex justify-between items-center">
                                <span>Setup Fee: <strong>${selectedTemplate.pricing.setup}</strong></span>
                                <span>Monthly: <strong>${selectedTemplate.pricing.monthly}</strong></span>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button className={`w-full ${variants?.primary}`} onClick={() => onCreateCommunity(selectedTemplate)}>
                                <Icons.Plus className="w-4 h-4" /> Create Community
                            </button>
                            <button className={`w-full ${variants?.outline}`} onClick={() => setSelectedTemplate(null)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </Section>
    );
};

const PricingPlans = ({ userPlan, onUpgrade }) => {
    const plans = [
        {
            name: "Free",
            price: 0,
            description: "Perfect for getting started",
            features: ["Up to 3 communities", "Basic analytics", "Standard support", "Community templates"],
            current: userPlan.type === "free"
        },
        {
            name: "Pro",
            price: 49,
            description: "For growing communities",
            features: ["Unlimited communities", "Advanced analytics", "Monetization tools", "Integration marketplace", "Priority support"],
            current: userPlan.type === "pro",
            popular: true
        },
        {
            name: "Enterprise",
            price: 199,
            description: "For large organizations",
            features: ["Everything in Pro", "White-label branding", "Custom integrations", "Dedicated support", "SLA guarantee"],
            current: userPlan.type === "enterprise"
        }
    ];

    return (
        <Section className="py-12">
            <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Choose Your Plan</h2>
                <p className="text-slate-600">Scale your communities with the right features for your needs</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
                {plans.map(plan => (
                    <Card key={plan.name} className={cls("p-8 text-center", plan.popular && "ring-2 ring-indigo-500 relative")}>
                        {plan.popular && (
                            <Badge variant="premium" className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                Most Popular
                            </Badge>
                        )}

                        <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                        <div className="text-3xl font-bold mb-2">
                            ${plan.price}
                            <span className="text-sm font-normal text-slate-500">/month</span>
                        </div>
                        <p className="text-slate-600 mb-6">{plan.description}</p>

                        <div className="space-y-3 mb-8">
                            {plan.features.map(feature => (
                                <div key={feature} className="flex items-center gap-2 text-sm">
                                    <Icons.Check className="w-4 h-4 text-emerald-600" />
                                    {feature}
                                </div>
                            ))}
                        </div>

                        {plan.current ? (
                            <button className={`w-full ${variants?.success}`} disabled>
                                <Icons.Check className="w-4 h-4" /> Current Plan
                            </button>
                        ) : (
                            <button

                                className={`w-full ${plan.popular ?
                                    variants.premium : variants.primary
                                    }`}
                                onClick={() => onUpgrade(plan.name.toLowerCase())}
                            >
                                <Icons.ArrowUp className="w-4 h-4" />
                                {plan.price === 0 ? "Downgrade" : "Upgrade"}
                            </button>
                        )}
                    </Card>
                ))}
            </div>
        </Section>
    );
};

/*********** Enhanced Screens ***********/
const LoginScreen = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [pending, setPending] = useState(false);
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white grid place-items-center">
            <Card className="p-8 w-full max-w-md bg-white/10 backdrop-blur border-white/20">
                <div className="flex items-center gap-3 mb-4">
                    <Icons.ShieldCheck className="w-6 h-6" />
                    <h1 className="text-2xl font-bold">Communities Hub</h1>
                </div>
                <p className="text-slate-300 mb-6">Sign in to access your communities and collaborate.</p>
                <label className="text-sm text-slate-200">Email</label>
                <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1 w-full rounded-xl px-3 py-2 bg-white/90 text-slate-900"
                />
                <button
                    className={cls(`flex items-center gap-2 bg-indigo-600 text-white px-1 py-2 rounded-lg hover:bg-indigo-700 transition ${variants.primary}`)}
                    onClick={async () => {
                        if (!email) return;
                        setPending(true);
                        await onLogin(email);
                        setPending(false);
                        window.location.hash = "#/app";
                    }}
                >
                    <Icons.LogIn className="w-4 h-4" /> {pending ? "Signing in..." : "Sign in"}
                </button>
                <div className="mt-4 text-xs text-slate-400">Mock auth for demo — wire to your backend/SSO later.</div>
            </Card>
        </div>
    );
};

const AppShell = ({ user, onLogout, userPlan, children, navigate }) => (
    <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-40 bg-white border-b border-slate-200">
            <Section className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600" />
                    <div className="font-extrabold">Communities Hub</div>
                    <Badge variant="premium">{userPlan.type}</Badge>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate("#/app")}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-indigo-600 transition"
                    >
                        <Icons.Home className="w-4 h-4" />
                        <span>Dashboard</span>
                    </button>

                    <button
                        onClick={() => navigate("#/templates")}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-indigo-600 transition"
                    >
                        <Icons.Layout className="w-4 h-4" />
                        <span>Templates</span>
                    </button>

                    <button
                        onClick={() => navigate("#/marketplace")}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-indigo-600 transition"
                    >
                        <Icons.Puzzle className="w-4 h-4" />
                        <span>Marketplace</span>
                    </button>

                    <button
                        onClick={() => navigate("#/pricing")}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-indigo-600 transition"
                    >
                        <Icons.Crown className="w-4 h-4" />
                        <span>Pricing</span>
                    </button>

                    <button
                        onClick={() => navigate("#/calendar")}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-indigo-600 transition"
                    >
                        <Icons.Calendar className="w-4 h-4" />
                        <span>Calendar</span>
                    </button>

                    <button
                        onClick={() => navigate("#/events")}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-indigo-600 transition"
                    >
                        <Icons.DollarSign className="w-4 h-4" />
                        <span>Paid Events</span>
                    </button>

                    <button
                        className="ml-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                        onClick={() => {
                            onLogout();
                            window.location.hash = "#/login";
                        }}
                    >
                        <Icons.LogOut className="w-4 h-4" />
                        <span>Logout</span>
                    </button>
                </div>

            </Section>
        </nav>
        <main>{children}</main>
    </div>
);

const Dashboard = ({ userPlan, navigate, communitylist }) => {

    console.log(communitylist, "communitylist")
    const [q, setQ] = useState("");
    const myIds = JSON.parse(localStorage.getItem(LS_MY_COMMUNITIES) || "[]");
    const mine = MOCK_COMMUNITIES.filter((c) => myIds.includes(c.id));
    const invites = MOCK_COMMUNITIES.filter((c) => !myIds.includes(c.id)).slice(0, 2);
    const filtered = mine.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

    const totalRevenue = mine.reduce((sum, c) => sum + c.monthlyRevenue, 0);
    const totalMembers = mine.reduce((sum, c) => sum + c.members, 0);

    return (
        <Section className="py-10">
            {/* Business Overview */}
            {userPlan.features.includes("monetization") || userPlan.features.includes("all-features") ? (
                <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Business Overview</h2>
                    <div className="grid md:grid-cols-4 gap-4">
                        <Card className="p-4">
                            <div className="flex items-center gap-2 text-emerald-600 mb-2">
                                <Icons.DollarSign className="w-5 h-5" />
                                <span className="font-semibold">Total Revenue</span>
                            </div>
                            <div className="text-2xl font-bold">${totalRevenue.toLocaleString()}</div>
                            <div className="text-sm text-emerald-600">+12% this month</div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-2 text-indigo-600 mb-2">
                                <Icons.Users className="w-5 h-5" />
                                <span className="font-semibold">Total Members</span>
                            </div>
                            <div className="text-2xl font-bold">{totalMembers.toLocaleString()}</div>
                            <div className="text-sm text-slate-500">Across {mine.length} communities</div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-2 text-purple-600 mb-2">
                                <Icons.Crown className="w-5 h-5" />
                                <span className="font-semibold">Premium Communities</span>
                            </div>
                            <div className="text-2xl font-bold">{mine.filter(c => c.isPremium).length}</div>
                            <div className="text-sm text-slate-500">Monetized</div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-2 text-amber-600 mb-2">
                                <Icons.TrendingUp className="w-5 h-5" />
                                <span className="font-semibold">Avg Engagement</span>
                            </div>
                            <div className="text-2xl font-bold">{Math.round(mine.reduce((sum, c) => sum + c.engagementRate, 0) / mine.length)}%</div>
                            <div className="text-sm text-slate-500">Across all communities</div>
                        </Card>
                    </div>
                </div>
            ) : null}

            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">My Communities</h2>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 border border-slate-300 rounded-xl px-3 py-1.5">
                        <Icons.Search className="w-4 h-4 text-slate-500" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="outline-none" />
                    </div>
                    <button
                        onClick={() => navigate("#/templates")}
                        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                    >
                        <Icons.Plus className="w-4 h-4" />
                        <span>New Community</span>
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
                {communitylist.map((c) => {
                    const Icon = resolveIcon(c.icon);
                    return (
                        <Card key={c.id} className="p-5 hover:shadow-xl transition-shadow">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <Icon className="w-6 h-6 text-indigo-600" />
                                    <div className="font-semibold">{c.Name}</div>
                                </div>
                                {c.IsActive && <Badge className={` ${variants?.premium || "premium"}`}>Premium</Badge>}
                            </div>

                            <div className="text-sm text-slate-600 line-clamp-2 mb-3">{c.Description || "sample"}</div>

{/*                           
                                <div className="flex items-center justify-between text-xs text-emerald-600 mb-3">
                                    <span>${c.monthlyRevenue || "monthlyRevenue"}/mo revenue</span>
                                    <span>{c.engagementRate || "engagementRate"}% engagement</span>
                                </div> */}
                           

                            <div className="flex items-center justify-between">
                                <div className="text-xs text-slate-500">
                                    <Icons.Users className="w-3 h-3 inline" /> {c.MembersCount || "0"}
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => navigate(`#/analytics?id=${c.id}`)} className={`text-xs px-2 py-1 ${variants?.outline}`}>
                                        <Icons.BarChart3 className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => navigate(`#/community?id=${c.id}`)} className={`flex items-center gap-2 bg-indigo-600 text-white px-1 py-2 rounded-lg hover:bg-indigo-700 transition ${variants?.primary}`}>
                                        Open <Icons.ChevronRight className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
           

            <h3 className="mt-10 text-lg font-semibold">Invitations</h3>
            <div className="grid md:grid-cols-2 gap-5 mt-3">
                {invites.map((c) => {
                    const Icon = resolveIcon(c.icon);
                    return (
                        <Card key={c.id} className="p-5">
                            <div className="flex items-center gap-3">
                                <Icon className="w-5 h-5 text-emerald-600" />
                                <div className="font-semibold">{c.name}</div>
                            </div>
                            <div className="mt-2 text-sm text-slate-600">{c.about}</div>
                            <div className="mt-3 flex gap-2">
                                <button className={` ${variants?.success}`} onClick={() => {
                                    const cur = new Set(JSON.parse(localStorage.getItem(LS_MY_COMMUNITIES) || "[]"));
                                    cur.add(c.id);
                                    localStorage.setItem(LS_MY_COMMUNITIES, JSON.stringify([...cur]));
                                    window.location.reload();
                                }}>Join</button>
                                <button className={` ${variants?.outline}`}>Dismiss</button>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </Section>
    );
};

const CalendarScreen = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Community Calendar</h1>
                    <p className="text-gray-600">Manage events, deadlines, and schedule across all your communities</p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Calendar Widget */}
                    <div className="lg:col-span-2">
                        <CalendarView />
                    </div>

                    {/* Sidebar with upcoming events */}
                    <div className="space-y-6">
                        <CalendarWidget />

                        {/* Quick Actions */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                            <div className="space-y-3">
                                <button className="w-full flex items-center space-x-3 p-3 text-left bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors">
                                    <Icons.Plus className="w-5 h-5" />
                                    <span>Create New Event</span>
                                </button>
                                <button className="w-full flex items-center space-x-3 p-3 text-left bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                                    <Icons.Download className="w-5 h-5" />
                                    <span>Export All Events</span>
                                </button>
                                <button className="w-full flex items-center space-x-3 p-3 text-left bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                                    <Icons.Calendar className="w-5 h-5" />
                                    <span>Sync with Google</span>
                                </button>
                                <button className="w-full flex items-center space-x-3 p-3 text-left bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors">
                                    <Icons.ExternalLink className="w-5 h-5" />
                                    <span>Connect Outlook</span>
                                </button>
                            </div>
                        </div>

                        {/* Calendar Statistics */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">This Month</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Events</span>
                                    <span className="font-semibold">24</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Upcoming Deadlines</span>
                                    <span className="font-semibold text-red-600">5</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Lessons Scheduled</span>
                                    <span className="font-semibold text-green-600">12</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Meetings Planned</span>
                                    <span className="font-semibold text-purple-600">7</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CommunityScreen = ({ id, navigate }) => {
    const community = MOCK_COMMUNITIES.find((c) => c.id === id);
    const [tab, setTab] = useState("feed");
    const [feed, setFeed] = useState(MOCK_FEED[id] || []);
    const [events, setEvents] = useState(MOCK_EVENTS[id] || []);
    const [showMonetization, setShowMonetization] = useState(false);
    const myIds = new Set(JSON.parse(localStorage.getItem(LS_MY_COMMUNITIES) || "[]"));
    const joined = myIds.has(id);

    if (!community) {
        return (
            <Section className="py-10">
                <Card className="p-6">Community not found.</Card>
            </Section>
        );
    }

    const post = async (text) => {
        const item = { id: Math.random().toString(36).slice(2), author: "You", text, ts: Date.now(), likes: 0, comments: 0, type: "general" };
        setFeed([item, ...feed]);
        await sleep(200);
    };

    const toggleJoin = () => {
        const cur = new Set(JSON.parse(localStorage.getItem(LS_MY_COMMUNITIES) || "[]"));
        if (cur.has(id)) cur.delete(id); else cur.add(id);
        localStorage.setItem(LS_MY_COMMUNITIES, JSON.stringify([...cur]));
        window.location.reload();
    };

    const rsvp = (eid) => setEvents(events.map((e) => (e.id === eid ? { ...e, rsvped: !e.rsvped } : e)));

    const Icon = resolveIcon(community.icon);

    return (
        <Section className="py-8">
            <button onClick={() => navigate("#/app")} className={`mb-4 ${variants?.outline}`}>
                <Icons.ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Icon className="w-7 h-7 text-indigo-600" />
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-bold">{community.name}</span>
                            {community.isPremium && <Badge className={` ${variants?.premium}`}>Premium</Badge>}
                        </div>
                        <div className="text-sm text-slate-500">{community.pillar} • {community.members} members</div>
                    </div>
                </div>
                <div className="flex gap-2">
                    {community.isPremium && (
                        <button className={` ${variants?.premium}`} onClick={() => setShowMonetization(true)}>
                            <Icons.DollarSign className="w-4 h-4" /> Monetization
                        </button>
                    )}
                    <button onClick={() => navigate(`#/analytics?id=${id}`)} className={`mb-4 ${variants?.outline}`}>
                        <Icons.BarChart3 className="w-4 h-4" /> Analytics
                    </button>
                    <button
                        className={` ${joined ? variants.outline : variants.primary}`}
                        onClick={toggleJoin}>
                        {joined ? "Leave" : "Join"}
                    </button>
                </div>
            </div>

            <div className="flex gap-2 flex-wrap mb-6">
                {[
                    { k: "feed", label: "Feed", icon: Icons.MessageSquare },
                    { k: "events", label: "Events", icon: Icons.Calendar },
                    { k: "members", label: "Members", icon: Icons.Users },
                    { k: "resources", label: "Resources", icon: Icons.BookOpen },
                    { k: "chat", label: "Chat", icon: Icons.MessageCircle }
                ].map((t) => (
                    <button
                        key={t.k}

                        className={` ${tab === t.k ? variants.primary : variants.outline
                            }`}

                        onClick={() => setTab(t.k)}
                    >
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </div>

            {/* Enhanced Tabs */}
            {tab === "feed" && (
                <div className="grid md:grid-cols-3 gap-6">
                    <Card className="p-4 md:col-span-2">
                        <NewPost onPost={post} />
                        <div className="mt-4 space-y-3">
                            {feed.map((f) => (
                                <Card key={f.id} className="p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm">
                                            <span className="font-semibold">{f.author}</span> •
                                            <span className="text-slate-500 ml-1">{new Date(f.ts).toLocaleString()}</span>
                                        </div>
                                        <Badge variant="default">{f.type}</Badge>
                                    </div>
                                    <div className="mb-3">{f.text}</div>
                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                        <button className="flex items-center gap-1 hover:text-indigo-600">
                                            <Icons.Heart className="w-4 h-4" /> {f.likes}
                                        </button>
                                        <button className="flex items-center gap-1 hover:text-indigo-600">
                                            <Icons.MessageCircle className="w-4 h-4" /> {f.comments}
                                        </button>
                                        <button className="flex items-center gap-1 hover:text-indigo-600">
                                            <Icons.Share className="w-4 h-4" /> Share
                                        </button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </Card>

                    <div className="space-y-4">
                        <Card className="p-4">
                            <div className="font-semibold mb-2">About</div>
                            <p className="text-sm text-slate-600">{community.about}</p>
                        </Card>

                        {community.isPremium && (
                            <Card className="p-4">
                                <div className="font-semibold mb-2 text-emerald-600">Premium Benefits</div>
                                <ul className="text-sm text-slate-600 space-y-1">
                                    <li>• Exclusive content access</li>
                                    <li>• Priority support</li>
                                    <li>• Advanced analytics</li>
                                    <li>• Direct mentor access</li>
                                </ul>
                                <button className={`w-full mt-3 ${variants?.premium}`}>
                                    <Icons.Crown className="w-4 h-4" /> Upgrade to Premium
                                </button>
                            </Card>
                        )}
                    </div>
                </div>
            )}

            {tab === "events" && (
                <div className="grid md:grid-cols-2 gap-4">
                    {events.map((e) => (
                        <Card key={e.id} className="p-4 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="font-semibold">{e.title}</div>
                                    <div className="text-sm text-slate-500">
                                        {new Date(e.date).toLocaleDateString()} at {e.time}
                                    </div>
                                    <Badge variant="default" className="mt-1">{e.type}</Badge>
                                </div>
                                {e.isPaid && (
                                    <Badge variant="premium">${e.price}</Badge>
                                )}
                            </div>

                            <div className="text-sm text-slate-600 mb-3">
                                <Icons.Users className="w-4 h-4 inline mr-1" />
                                {e.attendees} attending
                            </div>

                            <div className="flex gap-2">
                                <button
                                    className={`capitalize ${e.rsvped ?
                                        variants.success : variants.primary}`}
                                    onClick={() => rsvp(e.id)}
                                >
                                    {e.rsvped ? "Cancel RSVP" : (e.isPaid ? `RSVP ($${e.price})` : "RSVP")}
                                </button>
                                {e.isPaid && (
                                    <button className={`w-full mt-3 ${variants?.outline}`}>
                                        <Icons.Gift className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {tab === "members" && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold">Members ({community.members})</h3>
                        <button className={`w-full mt-3 ${variants?.primary}`}>
                            <Icons.UserPlus className="w-4 h-4" /> Invite Members
                        </button>
                    </div>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {["Priya", "Arun", "Karthik", "Sara", "Anita", "Rahul", "Maya", "Vikram"].map((m, i) => (
                            <div key={m} className="flex items-center gap-2 p-3 border border-slate-200 rounded-xl hover:bg-slate-50">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                                    {m[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-sm">{m}</div>
                                    {i < 3 && <Badge variant="premium" className="text-xs">Premium</Badge>}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === "resources" && (
                <div className="grid md:grid-cols-2 gap-6">
                    <Card className="p-6">
                        <h3 className="font-bold mb-4">Knowledge Base</h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                                <Icons.FileText className="w-5 h-5 text-indigo-600" />
                                <div>
                                    <div className="font-medium">Getting Started Guide</div>
                                    <div className="text-sm text-slate-500">Complete onboarding checklist</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                                <Icons.Video className="w-5 h-5 text-purple-600" />
                                <div>
                                    <div className="font-medium">Video Tutorials</div>
                                    <div className="text-sm text-slate-500">Step-by-step walkthroughs</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                                <Icons.Download className="w-5 h-5 text-emerald-600" />
                                <div>
                                    <div className="font-medium">Templates & Tools</div>
                                    <div className="text-sm text-slate-500">Ready-to-use resources</div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <h3 className="font-bold mb-4">Quick Links</h3>
                        <div className="space-y-2">
                            <a href="https://docs.example.com" className="flex items-center gap-2 text-indigo-600 hover:underline">
                                <Icons.ExternalLink className="w-4 h-4" />
                                API Documentation
                            </a>
                            <a href="https://support.example.com" className="flex items-center gap-2 text-indigo-600 hover:underline">
                                <Icons.HelpCircle className="w-4 h-4" />
                                Support Center
                            </a>
                            <a href="https://status.example.com" className="flex items-center gap-2 text-indigo-600 hover:underline">
                                <Icons.Activity className="w-4 h-4" />
                                System Status
                            </a>
                        </div>
                    </Card>
                </div>
            )}

            {tab === "chat" && (
                <Card className="p-6">
                    <div className="font-semibold mb-2">Live Chat</div>
                    <div className="text-sm text-slate-600 mb-4">
                        Real-time messaging for community members. Connect with Socket.IO, Ably, or your preferred chat service.
                    </div>
                    <button className={`w-full mt-3 ${variants?.primary}`}>
                        <Icons.MessageSquare className="w-4 h-4" /> Open Chat
                    </button>
                </Card>
            )}

            {/* Monetization Modal */}
            <Modal
                isOpen={showMonetization}
                onClose={() => setShowMonetization(false)}
                title="Monetization Dashboard"
            >
                <MonetizationDashboard community={community} />
            </Modal>
        </Section>
    );
};

const NewPost = ({ onPost }) => {
    const [text, setText] = useState("");
    const [postType, setPostType] = useState("general");

    const postTypes = [
        { value: "general", label: "General", icon: Icons.MessageSquare },
        { value: "question", label: "Question", icon: Icons.HelpCircle },
        { value: "resource", label: "Resource", icon: Icons.FileText },
        { value: "success", label: "Success", icon: Icons.Trophy }
    ];

    return (
        <div className="space-y-3">
            <div className="flex gap-2">
                {postTypes.map(type => {
                    const Icon = type.icon;
                    return (
                        <button
                            key={type.value}
                            className={` ${postType === type.value ? variants.primary : variants.outline}`}
                            onClick={() => setPostType(type.value)}
                        >
                            <Icon className="w-3 h-3" /> {type.label}
                        </button>
                    );
                })}
            </div>

            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                className="w-full border border-slate-300 rounded-xl px-3 py-2"
                placeholder="Share an update..."
            />

            <div className="flex justify-end">
                <button
                    className={`w-full mt-3 ${variants?.primary}`}
                    onClick={() => {
                        if (!text.trim()) return;
                        onPost(text.trim());
                        setText("");
                    }}
                >
                    <Icons.Plus className="w-4 h-4" /> Post
                </button>
            </div>
        </div>
    );
};

const AnalyticsScreen = ({ id, navigate }) => {
    const community = MOCK_COMMUNITIES.find(c => c.id === id);
    if (!community) return <Section className="py-10"><Card className="p-6">Community not found.</Card></Section>;

    return (
        <Section className="py-8">
            <button onClick={() => navigate("#/app")} className={`mb-4 ${variants?.outline}`}>
                <Icons.ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>

            <div className="flex items-center gap-3 mb-6">
                {React.createElement(resolveIcon(community.icon), { className: "w-6 h-6 text-indigo-600" })}
                <h2 className="text-2xl font-bold">{community.name} Analytics</h2>
                {community.isPremium && <Badge className={`w-full mt-3 ${variants?.primary}`}>Premium</Badge>}
            </div>

            <AnalyticsDashboard community={community} />
        </Section>
    );
};

const PaidEventsScreen = () => {
    return (
        <Section className="py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Paid Events Manager</h1>
                <p className="text-gray-600">Create, manage, and track revenue from your premium events</p>
            </div>
            {/* <PaidEventsManager /> */}
        </Section>
    );
};

/*********** Root ***********/
export default function CommunityHub() {
    const { user, login, logout, userPlan, upgradePlan } = useAuth();
    const { path, params } = useHashRoute();
    const { state } = useLocation();
    const navigate = useNavigate();
    const parmas: any = useParams()
    const dispatch = useAppDispatch()
    const communitiesItems = useSelector(selectCommunitiesListItems)
    const [pageData, setPageData] = useState(1)
    const [pageSize, setPageSize] = useState(10);



    console.log(state, "state")

    const createCommunityFromTemplate = (template) => {
        // In a real app, this would create a new community
        alert(`Creating community with ${template.name} template! Setup fee: $${template.pricing.setup}, Monthly: $${template.pricing.monthly}`);
        window.location.hash = "#/app";
    };

    // Basic tests
    useEffect(() => {
        // console.assert(Array.isArray(MOCK_COMMUNITIES) && MOCK_COMMUNITIES.length >= 5, "MOCK_COMMUNITIES seeded");
        console.assert(typeof login === "function" && typeof logout === "function", "auth functions present");
        console.assert(Array.isArray(COMMUNITY_TEMPLATES) && COMMUNITY_TEMPLATES.length >= 5, "Templates available");
        console.assert(Array.isArray(INTEGRATION_MARKETPLACE) && INTEGRATION_MARKETPLACE.length >= 6, "Integrations available");
    }, []);

    if (!user && path !== "#/login") window.location.hash = "#/login";
    if (!user) return <LoginScreen onLogin={login} />;

    //mycommunitylist
    const fetchCommunities = () => {
        dispatch(
            communities.list({
                url: `https://api.digiit.${process.env.REACT_APP_API_URL}/api/v6.1/e-appilcations/community/communities/list`,
                entity: "communities",
                jsonData: {
                    Context: {
                        Subscription: "ONE",
                        Domain: "ONE",
                        SubDomains: [],
                        Network: "Venture",
                        Application: "Venture",
                        Channel: "CSP",
                    },
                    Records: [
                        {
                            Id: 0,
                        },
                    ],
                    PageNumber: pageData,
                    PageSize: pageSize,
                    SortField: "Id",
                    SortOrder: "desc",
                },
            })
        );
    };

    // Fetch whenever pageData or pageSize changes
    useEffect(() => {
        fetchCommunities();
    }, [pageData, pageSize]); // safe because these come from local state

    const communitylist = communitiesItems?.result?.items;
    console.log(communitylist, "communitiesItems")

    return (
        <AppShell user={user} onLogout={logout} userPlan={userPlan} navigate={navigate}>
            {path === "#/app" && <Dashboard userPlan={userPlan} navigate={navigate} communitylist={communitylist} />}
            {path === "#/templates" && <CommunityTemplates userPlan={userPlan} onCreateCommunity={createCommunityFromTemplate} />}
            {path === "#/marketplace" && <IntegrationMarketplace userPlan={userPlan} />}
            {path === "#/pricing" && <PricingPlans userPlan={userPlan} onUpgrade={upgradePlan} />}
            {path === "#/calendar" && <CalendarScreen />}
            {path === "#/events" && <PaidEventsScreen />}
            {/* {path === "#/analytics" && <AnalyticsScreen id={params?.id || ""}  navigate={navigate} />}
            {path.startsWith("#/community") && <CommunityScreen id={params?.id || ""} navigate={navigate} />} */}
            {!["#/app", "#/templates", "#/marketplace", "#/pricing", "#/calendar", "#/events", "#/analytics", "#/community"].some(p => path.startsWith(p)) && <Dashboard userPlan={userPlan} communitylist={communitylist} navigate={navigate} />}
        </AppShell>
    );
}

